import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
    Paperclip, X, ChevronLeft, ChevronRight,
    Search, Database, Hash, Code2, FileText,
    ChevronDown, ChevronUp, Filter, LayoutGrid, List
} from 'lucide-react';

const RECORDS_PER_PAGE = 20;

function formatValue(v) {
    if (v === null || v === undefined) return { text: 'N/A', type: 'null' };
    if (typeof v === 'object') {
        try { return { text: JSON.stringify(v, null, 2), type: 'json' }; }
        catch { return { text: String(v), type: 'string' }; }
    }
    if (typeof v === 'string') {
        const trimmed = v.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try { return { text: JSON.stringify(JSON.parse(trimmed), null, 2), type: 'json' }; }
            catch { }
        }
        return { text: v, type: 'string' };
    }
    if (typeof v === 'number') return { text: String(v), type: 'number' };
    if (typeof v === 'boolean') return { text: String(v), type: 'boolean' };
    return { text: String(v), type: 'string' };
}

function ValueBadge({ type }) {
    const map = {
        json: { label: 'JSON', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/25' },
        string: { label: 'STR', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/25' },
        number: { label: 'NUM', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' },
        boolean: { label: 'BOOL', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/25' },
        null: { label: 'NULL', cls: 'bg-white/5 text-white/25 border-white/10' },
    };
    const { label, cls } = map[type] || map.string;
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider border font-mono flex-shrink-0 ${cls}`}>
            {label}
        </span>
    );
}

function RecordCard({ record, index, globalIndex }) {
    const [expandedKeys, setExpandedKeys] = useState(new Set());

    const toggleKey = (k) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            next.has(k) ? next.delete(k) : next.add(k);
            return next;
        });
    };

    const entries = Object.entries(record ?? {});

    return (
        <div className="rounded-xl border border-white/10 overflow-hidden bg-gradient-to-b from-white/[0.04] to-white/[0.02]">
            {/* Card header */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border-b border-white/8">
                <span className="w-5 h-5 rounded-md bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-[10px] font-bold text-blue-300 font-mono flex-shrink-0">
                    {globalIndex + 1}
                </span>
                <span className="text-[10px] text-white/30 font-mono tracking-wider uppercase">Record #{globalIndex + 1}</span>
                <span className="ml-auto text-[9px] text-white/20 font-mono">{entries.length} fields</span>
            </div>

            {/* Fields grid */}
            <div className="divide-y divide-white/5">
                {entries.map(([k, v]) => {
                    const { text, type } = formatValue(v);
                    const isLong = text.length > 60 || text.includes('\n');
                    const isExpanded = expandedKeys.has(k);

                    return (
                        <div key={k} className="group">
                            <div
                                className={`flex items-start gap-3 px-4 py-2.5 ${isLong ? 'cursor-pointer hover:bg-white/[0.03] active:bg-white/5' : ''}`}
                                onClick={() => isLong && toggleKey(k)}
                            >
                                {/* Key */}
                                <div className="flex items-center gap-2 flex-shrink-0 w-32 sm:w-40 pt-0.5">
                                    <ValueBadge type={type} />
                                    <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wider leading-tight truncate">
                                        {k.replace(/_/g, ' ')}
                                    </span>
                                </div>

                                {/* Value */}
                                <div className="flex-1 min-w-0">
                                    {type === 'null' ? (
                                        <span className="text-xs text-white/20 italic">null</span>
                                    ) : isLong && !isExpanded ? (
                                        <span className="text-xs font-mono text-white/60 truncate block">
                                            {text.slice(0, 80)}{text.length > 80 ? '…' : ''}
                                        </span>
                                    ) : isLong && isExpanded ? (
                                        <pre className="text-xs font-mono text-white/80 whitespace-pre-wrap break-all leading-relaxed bg-black/30 rounded-lg p-3 mt-1 max-h-48 overflow-y-auto border border-white/8">
                                            {text}
                                        </pre>
                                    ) : (
                                        <span className={`text-xs font-mono ${
                                            type === 'number' ? 'text-emerald-300' :
                                            type === 'boolean' ? 'text-amber-300' :
                                            'text-white/80'
                                        }`}>{text}</span>
                                    )}
                                </div>

                                {/* Expand toggle */}
                                {isLong && (
                                    <div className="flex-shrink-0 pt-0.5">
                                        {isExpanded
                                            ? <ChevronUp className="w-3 h-3 text-white/20 group-hover:text-white/50 transition-colors" />
                                            : <ChevronDown className="w-3 h-3 text-white/20 group-hover:text-white/50 transition-colors" />
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Pagination({ currentPage, totalPages, totalRecords, onPage }) {
    const start = currentPage * RECORDS_PER_PAGE + 1;
    const end = Math.min((currentPage + 1) * RECORDS_PER_PAGE, totalRecords);

    // Build page number array with ellipsis
    const getPages = () => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
        const pages = [];
        if (currentPage <= 3) {
            pages.push(0, 1, 2, 3, 4, '...', totalPages - 1);
        } else if (currentPage >= totalPages - 4) {
            pages.push(0, '...', totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
        } else {
            pages.push(0, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages - 1);
        }
        return pages;
    };

    return (
        <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-3 border-t border-white/10 bg-white/[0.02]">
            <span className="text-[11px] text-white/35 font-mono">
                Showing <span className="text-white/60">{start}–{end}</span> of <span className="text-white/60">{totalRecords}</span> records
            </span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPage(0)}
                    disabled={currentPage === 0}
                    className="h-7 px-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed text-white/60 text-xs transition-colors border border-white/8 font-mono"
                >«</button>
                <button
                    onClick={() => onPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="h-7 px-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed transition-colors border border-white/8"
                >
                    <ChevronLeft className="w-3.5 h-3.5 text-white/60" />
                </button>

                <div className="flex items-center gap-1 mx-1">
                    {getPages().map((p, i) =>
                        p === '...' ? (
                            <span key={`ellipsis-${i}`} className="w-7 h-7 flex items-center justify-center text-white/25 text-xs">…</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => onPage(p)}
                                className={`w-7 h-7 rounded-lg text-xs font-mono transition-all border ${
                                    p === currentPage
                                        ? 'bg-blue-500/25 border-blue-500/40 text-blue-200 font-bold'
                                        : 'bg-white/5 border-white/8 text-white/40 hover:bg-white/10 hover:text-white/70'
                                }`}
                            >
                                {p + 1}
                            </button>
                        )
                    )}
                </div>

                <button
                    onClick={() => onPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="h-7 px-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed transition-colors border border-white/8"
                >
                    <ChevronRight className="w-3.5 h-3.5 text-white/60" />
                </button>
                <button
                    onClick={() => onPage(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="h-7 px-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed text-white/60 text-xs transition-colors border border-white/8 font-mono"
                >»</button>
            </div>
        </div>
    );
}

function AttachmentViewerModal({ title, data, onClose }) {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const filtered = data.filter(record =>
        JSON.stringify(record).toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / RECORDS_PER_PAGE));
    const safePage = Math.min(page, totalPages - 1);
    const pageRecords = filtered.slice(safePage * RECORDS_PER_PAGE, (safePage + 1) * RECORDS_PER_PAGE);

    const handleSearch = (val) => {
        setSearch(val);
        setPage(0);
    };

    const handlePage = (p) => {
        setPage(Math.max(0, Math.min(totalPages - 1, p)));
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 flex items-center justify-center p-2 sm:p-4"
            style={{ zIndex: 200000 }}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/75 backdrop-blur-md"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                className="relative flex flex-col w-full bg-slate-950 border border-white/12 rounded-2xl shadow-2xl overflow-hidden"
                style={{
                    maxWidth: '1100px',
                    height: '90vh',
                    zIndex: 200001,
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 32px 80px rgba(0,0,0,0.8)',
                }}
            >
                {/* ── Header ── */}
                <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-white/8 bg-gradient-to-r from-white/[0.04] to-transparent">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500/30 to-blue-600/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                        <Database className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-bold text-white tracking-tight truncate">{title}</h2>
                        <p className="text-[10px] text-white/35 font-mono">
                            {data.length} total records &nbsp;·&nbsp; {RECORDS_PER_PAGE} per page
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 w-8 h-8 rounded-full bg-white/8 hover:bg-white/15 border border-white/10 flex items-center justify-center transition-all hover:rotate-90 duration-200"
                    >
                        <X className="w-3.5 h-3.5 text-white/70" />
                    </button>
                </div>

                {/* ── Search bar ── */}
                <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-white/6 bg-white/[0.015]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25 pointer-events-none" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => handleSearch(e.target.value)}
                            placeholder="Search across all fields and values…"
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-blue-500/40 focus:bg-white/8 transition-all"
                        />
                        {search && (
                            <button
                                onClick={() => handleSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    {search && (
                        <p className="mt-1.5 text-[11px] text-white/35 font-mono px-1">
                            {filtered.length} record{filtered.length !== 1 ? 's' : ''} matched
                            {filtered.length === 0 && ' — try a different keyword'}
                        </p>
                    )}
                </div>

                {/* ── Records grid ── */}
                {filtered.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-white/20">
                        <Search className="w-10 h-10 opacity-30" />
                        <p className="text-sm">No records match your search</p>
                        <button
                            onClick={() => handleSearch('')}
                            className="text-xs text-blue-400/60 hover:text-blue-400 underline transition-colors"
                        >
                            Clear search
                        </button>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto overscroll-contain">
                        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-min">
                            {pageRecords.map((record, i) => (
                                <RecordCard
                                    key={`${safePage}-${i}`}
                                    record={record}
                                    index={i}
                                    globalIndex={safePage * RECORDS_PER_PAGE + i}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Pagination ── */}
                {filtered.length > 0 && (
                    <Pagination
                        currentPage={safePage}
                        totalPages={totalPages}
                        totalRecords={filtered.length}
                        onPage={handlePage}
                    />
                )}
            </div>
        </div>,
        document.body
    );
}

export default AttachmentViewerModal;